import { ChangeDetectorRef, Component, Injector, OnDestroy } from '@angular/core';
import { MessagingAbstract } from '../../../../_models/messaging/messaging-abstract';
import { ExecutionResultDto } from '../../../../_models/execution-result-model';
import { ExecutionResult } from '../../../../_models/execution-result-enum';
import { MessageReportsService } from '../../../../_services/messaging/messages/message-reports.service';
import {
  SMSMessageReportsRetrievingModel
} from '../../../../_models/messaging/messages/message-reports/sms-reports/sms-message-reports-retrieving-model';
import {
  SMSMessageRunReportModel
} from '../../../../_models/messaging/messages/message-reports/sms-reports/sms-message-run-report-model';
import { DateService } from '../../../../_services/system/date.service';
import {
  SMSMessageRunReportModelAdapter
} from '../../../../_models/messaging/messages/message-reports/sms-reports/sms-message-run-report-model.adapter';
import { MessageFolderService } from '../../../../_services/messaging/messages/message-folder.service';
import { TableActionType } from '../../../shared/constants/table-constants';
import { HelperService } from '../../../../_services/system/helpers/helper.service';
import { DataTypesEnum } from 'src/app/_models/selected-fields-filters/data-types-enum';
import { PagerModel } from 'src/app/_models/system/pager-model';

@Component({
  selector: 'app-rcs-reports',
  templateUrl: './rcs-reports.component.html',
  styleUrls: ['./rcs-reports.component.scss'],
})
export class RcsReportsComponent extends MessagingAbstract implements OnDestroy {
  displayedColumns = [
    { name: 'listId', displayName: 'List ID' },
    { name: 'messageName', displayName: 'Message Name' },
    { name: 'messageSourceName', displayName: 'Message Source Name' },
    { name: 'sentBy', displayName: 'Sent By' },
    { name: 'folderName', displayName: 'Folder Name' },
    { name: 'sendDate', displayName: 'Send Date' },
    { name: 'totalSent', displayName: 'Total Sent' },
    { name: 'totalDelivered', displayName: 'Total Delivered' },
    { name: 'deliveredRate', displayName: 'Delivered Rate (%)' },
    { name: 'totalBounces', displayName: 'Total Bounces' },
    { name: 'bounceRate', displayName: 'Bounce Rate (%)' },
    { name: 'actions', displayName: '' }
  ];

  rcsReportModel: SMSMessageReportsRetrievingModel;
  dataType = DataTypesEnum.RcsReports;

  _exportCancelled = false;
  _exporting = false;
  _exportPercent = 0;
  _exportMessage = '';

  private _listSortColumn = 'ChangeDate';
  private _listSortDirection = 'Descending';

  constructor(
    public messageReportsService: MessageReportsService,
    private dateService: DateService,
    private smsMessageRunReportModelAdapter: SMSMessageRunReportModelAdapter,
    public messageFolderService: MessageFolderService,
    private helperService: HelperService,
    injector: Injector,
    private cdr: ChangeDetectorRef
  ) {
    super(injector);
    this.tableKey = 'rcsReports';
    this.init();
  }

  ngOnDestroy(): void {
    this._exportCancelled = true;
  }

  getReports(data): void {
    if (data?.sortColumn) {
      this._listSortColumn = data.sortColumn;
    }
    if (data?.sortDirection) {
      this._listSortDirection = data.sortDirection;
    }

    this.loadingSpinnerService.loading();
    this.rcsReportModel = {
      PagerModel: {
        Page: data.currentPage,
        PageSize: data.pageLimit,
        SortColumn: data.sortColumn,
        SortDirection: data.sortDirection
      },
      FolderID: this.selectedFolderId
    };

    this.messageReportsService.getRCSReports(this.rcsReportModel).subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        this.data = this.smsMessageRunReportModelAdapter.adaptArray(executionResultDto.data.Item1);
        this.length = executionResultDto.data.Item2;
        this.formatDate();
        this.roundRates();
        this.loadingSpinnerService.stopLoading();
      } else {
        this.notificationService.showError(executionResultDto.message);
        this.loadingSpinnerService.stopLoading();
      }
    });
  }

  formatDate() {
    this.data.forEach((report: SMSMessageRunReportModel) => {
      report.sendDate = this.dateService.formatStringDate(report.sendDate);
    });
  }

  formatDateRows(rows: SMSMessageRunReportModel[]) {
    rows.forEach((report: SMSMessageRunReportModel) => {
      report.sendDate = this.dateService.formatStringDate(report.sendDate);
    });
  }

  roundRates() {
    this.data.forEach((report: SMSMessageRunReportModel) => {
      report.bounceRate = (+report.bounceRate * 100).toFixed(0);
      report.deliveredRate = (+report.deliveredRate * 100).toFixed(0);
    });
  }

  roundRatesRows(rows: SMSMessageRunReportModel[]) {
    rows.forEach((report: SMSMessageRunReportModel) => {
      report.bounceRate = (+report.bounceRate * 100).toFixed(0);
      report.deliveredRate = (+report.deliveredRate * 100).toFixed(0);
    });
  }

  actionHandler({ selectedRows, action }): void {
    switch (TableActionType[action]) {
      case TableActionType.openDetails:
        this.router.navigate([ 'rcs-reports/details/', selectedRows.reportId]);
        break;
      case TableActionType.export:
        this.messageReportsService.export(this.data, selectedRows.map(row => {
          return {
            displayName: row,
            name: this.displayedColumns.find(x => x.displayName === row)?.name
          }
        }), false, true);
        break;
      case TableActionType.exportAllView:
        this.exportAllViewChunked(Array.isArray(selectedRows) ? selectedRows : []);
        break;
    }
  }

  private exportAllViewChunked(columnDisplayNames: string[]): void {
    const total = this.length;
    if (!total || total < 1) {
      this.notificationService.showWarning('No data to export.');
      return;
    }

    const safeColumns = (columnDisplayNames ?? []).filter((c) => c && String(c).trim().length > 0);
    if (safeColumns.length < 1) {
      this.notificationService.showWarning('Select at least one column to export.');
      return;
    }

    if (this._exporting) {
      this.notificationService.showWarning('An export is already in progress.');
      return;
    }

    const mapped = safeColumns.map((row) => ({
      displayName: row,
      name: this.displayedColumns.find((x) => x.displayName === row)?.name
    }));
    mapped.pop();
    const exportCols = mapped.filter((c) => c.name);
    if (exportCols.length < 1) {
      this.notificationService.showWarning('Select at least one column to export.');
      return;
    }

    this._exportCancelled = false;
    this._exporting = true;
    this._exportPercent = 0;
    this._exportMessage = 'About 0% loaded';
    this.cdr.detectChanges();

    const chunkSize = 5000;
    const totalChunkRequests = Math.max(1, Math.ceil(total / chunkSize));
    const maxPages = totalChunkRequests + 50;
    const allRecords: SMSMessageRunReportModel[] = [];
    let page = 1;

    const refreshExportProgress = () => {
      const loaded = allRecords.length;
      let pct = 0;
      if (total > 0 && loaded > 0) {
        pct = Math.ceil((loaded / total) * 100);
        if (loaded < total) {
          pct = Math.min(99, Math.max(1, pct));
        } else {
          pct = 100;
        }
      }
      this._exportPercent = pct;
      this._exportMessage = `About ${pct}% loaded`;
      this.cdr.detectChanges();
    };

    const finishExport = (onFinally?: () => void) => {
      this._exporting = false;
      this._exportPercent = 0;
      this._exportMessage = '';
      this.cdr.detectChanges();
      onFinally?.();
    };

    const writeCsv = (rows: SMSMessageRunReportModel[]) => {
      const capped = rows.length > total ? rows.slice(0, total) : rows;
      const csvRows = capped.map((row) => {
        const exportRow: { [key: string]: any } = {};
        exportCols.forEach((col) => {
          exportRow[col.displayName] = row[col.name];
        });
        return exportRow;
      });
      const today = new Date();
      const fileName = `RCSReport_${today.getDate()}${today.getMonth() + 1}${today.getFullYear()}`;
      this.helperService.exportToCSV(fileName, csvRows);
      this.helperService.sentEmailWarningNotificationOfFileDownload('RCS Report', today);
    };

    const tryWriteCsvAndFinish = (rows: SMSMessageRunReportModel[], onFinally?: () => void) => {
      try {
        writeCsv(rows);
        finishExport(onFinally);
      } catch {
        finishExport(() =>
          this.notificationService.showError('Could not build or download the CSV file. Please try again.')
        );
      }
    };

    const fetchNext = () => {
      if (this._exportCancelled) {
        finishExport();
        return;
      }

      if (page > maxPages) {
        if (allRecords.length > 0) {
          tryWriteCsvAndFinish(allRecords, () =>
            this.notificationService.showWarning(
              `Export stopped after ${maxPages} page requests. Saved ${allRecords.length.toLocaleString()} rows (total was ${total.toLocaleString()}).`
            )
          );
        } else {
          finishExport(() =>
            this.notificationService.showError(
              'Export stopped: too many empty pages. Try again or contact support.'
            )
          );
        }
        return;
      }

      const reportModel = new SMSMessageReportsRetrievingModel({
        FolderID: this.selectedFolderId,
        PagerModel: new PagerModel({
          Page: page,
          PageSize: chunkSize,
          SortColumn: this._listSortColumn,
          SortDirection: this._listSortDirection
        })
      });

      this.messageReportsService.getRCSReports(reportModel).subscribe({
        next: (executionResultDto: ExecutionResultDto) => {
          if (this._exportCancelled) {
            finishExport();
            return;
          }

          if (executionResultDto.executionResult !== ExecutionResult.success) {
            finishExport(() =>
              this.notificationService.showError(executionResultDto.message || 'The export request failed.')
            );
            return;
          }

          if (!executionResultDto.data) {
            finishExport(() => this.notificationService.showError('Invalid response from server.'));
            return;
          }

          const raw = executionResultDto.data.Item1 ?? [];
          const chunk = this.smsMessageRunReportModelAdapter.adaptArray(raw);
          this.formatDateRows(chunk);
          this.roundRatesRows(chunk);
          allRecords.push(...chunk);

          refreshExportProgress();

          if (allRecords.length >= total) {
            this._exportPercent = 100;
            this._exportMessage = 'About 100% — preparing download';
            this.cdr.detectChanges();
            tryWriteCsvAndFinish(allRecords);
            return;
          }

          if (chunk.length === 0) {
            if (allRecords.length === 0) {
              finishExport(() => this.notificationService.showError('No rows returned for export.'));
            } else {
              tryWriteCsvAndFinish(allRecords, () => {
                if (allRecords.length < total) {
                  this.notificationService.showWarning(
                    `Exported ${allRecords.length.toLocaleString()} rows; the report total was ${total.toLocaleString()}.`
                  );
                }
              });
            }
            return;
          }

          page += 1;
          fetchNext();
        },
        error: () => {
          finishExport(() => this.notificationService.showError('Could not export the report. Please try again.'));
        }
      });
    };

    fetchNext();
  }
}
